<template>
  <button
    class="ai-gradient-button"
    :class="`ai-gradient-button--${state}`"
    :disabled="loading"
    @click="$emit('click')"
  >
    <span
      v-if="rippleKey"
      :key="rippleKey"
      class="ai-gradient-button__ripple"
    ></span>
    <span
      class="ai-gradient-button__spinner"
      :class="{ 'is-static': !loading }"
    ></span>
    <span class="ai-gradient-button__copy">
      <strong>{{ label }}</strong>
      <small>{{ hint }}</small>
    </span>
  </button>
</template>

<script lang="ts">
import { defineComponent, type PropType } from 'vue';

type VerificationState = 'idle' | 'verifying' | 'verified' | 'error';

export default defineComponent({
  name: 'AiGradientButton',
  props: {
    label: {
      type: String,
      required: true,
    },
    hint: {
      type: String,
      required: true,
    },
    loading: {
      type: Boolean,
      required: true,
    },
    state: {
      type: String as PropType<VerificationState>,
      required: true,
    },
    rippleKey: {
      type: Number,
      required: true,
    },
  },
  emits: ['click'],
});
</script>

<style scoped>
.ai-gradient-button {
  position: relative;
  width: 100%;
  min-height: 106px;
  display: grid;
  grid-template-columns: auto 1fr;
  align-items: center;
  gap: 18px;
  padding: 22px 30px;
  border: 1px solid rgba(222, 241, 255, 0.95);
  border-radius: 30px;
  background: linear-gradient(90deg, #607cff 0%, #5a94ff 42%, #72d8ff 100%);
  box-shadow:
    0 24px 48px rgba(106, 149, 235, 0.24),
    0 0 32px rgba(114, 216, 255, 0.2),
    inset 0 1px 0 rgba(255, 255, 255, 0.48);
  cursor: pointer;
  overflow: hidden;
  transition:
    transform 0.26s ease,
    box-shadow 0.26s ease,
    filter 0.26s ease;
}

.ai-gradient-button::before {
  content: '';
  position: absolute;
  inset: 0;
  background: linear-gradient(
    118deg,
    transparent 22%,
    rgba(255, 255, 255, 0.38) 48%,
    transparent 74%
  );
  transform: translateX(-120%);
  animation: buttonShine 4.4s ease-in-out infinite;
}

.ai-gradient-button--idle {
  background: linear-gradient(90deg, #a9bcf2 0%, #94b8f5 50%, #8ad0f2 100%);
  box-shadow:
    0 18px 36px rgba(141, 171, 223, 0.18),
    0 0 24px rgba(114, 216, 255, 0.12),
    inset 0 1px 0 rgba(255, 255, 255, 0.52);
}

.ai-gradient-button--idle::before {
  opacity: 0.45;
}

.ai-gradient-button--error {
  background: linear-gradient(90deg, #7c95f7 0%, #70a3ff 42%, #7ad6ef 100%);
}

.ai-gradient-button:hover:not(:disabled) {
  transform: translateY(-3px) scale(1.01);
  filter: brightness(1.03);
  box-shadow:
    0 28px 54px rgba(103, 150, 238, 0.3),
    0 0 40px rgba(114, 216, 255, 0.28),
    inset 0 1px 0 rgba(255, 255, 255, 0.54);
}

.ai-gradient-button:disabled {
  cursor: wait;
}

.ai-gradient-button__spinner {
  position: relative;
  z-index: 1;
  width: 28px;
  height: 28px;
  border-radius: 999px;
  border: 3px solid rgba(255, 255, 255, 0.38);
  border-top-color: #ffffff;
  animation: buttonSpin 1s linear infinite;
}

.ai-gradient-button__spinner.is-static {
  animation: none;
  opacity: 0.78;
}

.ai-gradient-button__copy {
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  color: #ffffff;
  text-align: left;
}

.ai-gradient-button__copy strong {
  font-size: 22px;
  font-weight: 800;
  letter-spacing: 0.06em;
}

.ai-gradient-button__copy small {
  margin-top: 6px;
  color: rgba(255, 255, 255, 0.92);
  font-size: 15px;
}

.ai-gradient-button__ripple {
  position: absolute;
  inset: 50%;
  width: 16px;
  height: 16px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.6);
  transform: translate(-50%, -50%);
  animation: rippleExpand 0.85s ease-out forwards;
}

@keyframes buttonShine {
  0%,
  72% {
    transform: translateX(-120%);
  }
  100% {
    transform: translateX(120%);
  }
}

@keyframes buttonSpin {
  to {
    transform: rotate(360deg);
  }
}

@keyframes rippleExpand {
  from {
    opacity: 0.75;
    transform: translate(-50%, -50%) scale(1);
  }
  to {
    opacity: 0;
    transform: translate(-50%, -50%) scale(20);
  }
}

@media (max-width: 768px) {
  .ai-gradient-button {
    min-height: 88px;
    padding: 18px 20px;
  }

  .ai-gradient-button__copy strong {
    font-size: 18px;
  }

  .ai-gradient-button__copy small {
    font-size: 13px;
  }
}

@media (max-width: 768px) and (orientation: portrait) {
  .ai-gradient-button {
    min-height: 56px;
    gap: 10px;
    padding: 10px 14px;
    border-radius: 18px;
  }

  .ai-gradient-button__spinner {
    width: 18px;
    height: 18px;
    border-width: 2px;
  }

  .ai-gradient-button__copy strong {
    font-size: 14px;
  }

  .ai-gradient-button__copy small {
    margin-top: 2px;
    font-size: 10px;
    line-height: 1.25;
  }
}

@media (orientation: landscape) and (max-height: 820px) {
  .ai-gradient-button {
    min-height: 70px;
    gap: 12px;
    padding: 14px 18px;
    border-radius: 22px;
  }

  .ai-gradient-button__spinner {
    width: 22px;
    height: 22px;
    border-width: 2px;
  }

  .ai-gradient-button__copy strong {
    font-size: 16px;
  }

  .ai-gradient-button__copy small {
    margin-top: 4px;
    font-size: 12px;
  }
}

@media (max-width: 480px) {
  .ai-gradient-button {
    grid-template-columns: 1fr;
    justify-items: center;
    gap: 12px;
  }

  .ai-gradient-button__copy {
    align-items: center;
    text-align: center;
  }
}
</style>
